import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Save, X, Briefcase, Loader2 } from 'lucide-react';
import { jobBoardApi } from '../../api/jobBoardApi';
import { JobDto } from '../../types/jobBoard';

const JobForm = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);

  const [formData, setFormData] = useState<Partial<JobDto>>({
    jobTitle: '',
    jobDescription: '',
    qualifications: '',
    numberOfPeopleNeeded: 1,
  });

  useEffect(() => {
    if (id) {
      const fetchJob = async () => {
        setFetching(true);
        try {
          const res = await jobBoardApi.getJob(id);
          setFormData(res.data);
        } catch (err) {
          console.error("Error loading job:", err);
        } finally {
          setFetching(false);
        }
      };
      fetchJob();
    }
  }, [id]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (id) {
        await jobBoardApi.updateJob(id, formData);
      } else {
        await jobBoardApi.createJob(formData);
      }
      navigate('/job-board/jobs');
    } catch (err) {
      console.error("Error saving job:", err);
    } finally {
      setLoading(false);
    }
  };

  if (fetching) return <div className="p-8 flex justify-center"><Loader2 className="animate-spin text-indigo-600" /></div>;

  return (
    <div className="p-4 lg:p-8 max-w-3xl mx-auto w-full">
      <header className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2 leading-tight mb-0">
            <Briefcase className="text-indigo-600" size={24} />
            {id ? 'Edit Job Posting' : 'Create New Job'}
          </h1>
          <p className="text-slate-500 text-sm mt-1">Set up position details, description, and requirements.</p>
        </div>
        <button onClick={() => navigate(-1)} className="p-2 hover:bg-slate-100 rounded-full transition"><X size={20} /></button>
      </header>

      <form onSubmit={handleSubmit} className="bg-white p-8 rounded-3xl border border-slate-100 shadow-sm space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="md:col-span-2">
            <label className="block text-sm font-semibold text-slate-700 mb-2">Job Title</label>
            <input 
              type="text" 
              value={formData.jobTitle}
              onChange={e => setFormData({ ...formData, jobTitle: e.target.value })}
              className="w-full p-3 bg-slate-50 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none transition" 
              required 
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">People Needed</label>
            <input 
              type="number" 
              value={formData.numberOfPeopleNeeded}
              onChange={e => setFormData({ ...formData, numberOfPeopleNeeded: Number(e.target.value) })}
              className="w-full p-3 bg-slate-50 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none transition" 
              required
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-2">Description</label>
          <textarea 
            rows={4} 
            value={formData.jobDescription}
            onChange={e => setFormData({ ...formData, jobDescription: e.target.value })}
            className="w-full p-3 bg-slate-50 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none transition" 
            required
          ></textarea>
        </div>

        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-2">Qualifications</label>
          <textarea 
            rows={4} 
            value={formData.qualifications}
            onChange={e => setFormData({ ...formData, qualifications: e.target.value })}
            className="w-full p-3 bg-slate-50 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none transition" 
            required
          ></textarea>
        </div>

        <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-100">
          <button type="submit" disabled={loading} className="px-6 py-2.5 bg-indigo-600 text-white font-bold rounded-xl hover:bg-indigo-700 transition flex items-center gap-2">
            {loading ? <Loader2 className="animate-spin" size={18} /> : <Save size={18} />}
            {id ? 'Update Posting' : 'Save Posting'}
          </button>
        </div>
      </form>
    </div>
  );
};
export default JobForm;